/**
 * Composio Settings — manage external app integrations via Composio.
 *
 * Scaffold: shows backend reachability, lists connected integrations, and
 * surfaces available apps from the Composio catalog. OAuth connect flow is
 * stubbed pending backend redirect endpoints.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Plug,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  composioApi,
  type ComposioApp,
  type ComposioIntegration,
} from '@/lib/api/client'

type BackendStatus = 'checking' | 'ok' | 'not-configured' | 'error'

function getAppKey(app: ComposioApp): string {
  // Composio v0.6 toolkits use `slug` as the stable identifier.
  return (
    ((app as { slug?: string }).slug) ||
    (app.appId as string | undefined) ||
    (app.key as string | undefined) ||
    (app.name as string | undefined) ||
    ''
  )
}

function getAppLabel(app: ComposioApp): string {
  return (
    (app.displayName as string | undefined) ||
    (app.name as string | undefined) ||
    getAppKey(app) ||
    'Unknown app'
  )
}

function getAppDescription(app: ComposioApp): string | undefined {
  // v0.6 toolkits nest description under `meta`; older shapes expose it
  // at the top level.
  const meta = (app as { meta?: { description?: string } }).meta
  return (app.description as string | undefined) ?? meta?.description
}

function getAppLogo(app: ComposioApp): string | undefined {
  const meta = (app as { meta?: { logo?: string } }).meta
  return (app.logo as string | undefined) ?? meta?.logo
}

export function ComposioSettings() {
  const [status, setStatus] = useState<BackendStatus>('checking')
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  const [apps, setApps] = useState<ComposioApp[]>([])
  const [integrations, setIntegrations] = useState<ComposioIntegration[]>([])
  const [integrationsError, setIntegrationsError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const loadAll = useCallback(async () => {
    setRefreshing(true)
    setStatus('checking')
    setStatusMessage(null)
    setIntegrationsError(null)

    const appsPromise = composioApi.listApps()
    const connectionsPromise = composioApi.listConnections()

    try {
      const appsResult = await appsPromise
      setApps(appsResult ?? [])
      setStatus('ok')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      if (/COMPOSIO_API_KEY/i.test(msg)) {
        setStatus('not-configured')
      } else {
        setStatus('error')
      }
      setStatusMessage(msg)
      setApps([])
    }

    try {
      const connectionsResult = await connectionsPromise
      setIntegrations(connectionsResult ?? [])
    } catch (err) {
      setIntegrationsError(
        err instanceof Error ? err.message : 'Failed to load integrations',
      )
      setIntegrations([])
    }

    setRefreshing(false)
  }, [])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  const filteredApps = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return apps
    return apps.filter((a) => {
      const label = getAppLabel(a).toLowerCase()
      const key = getAppKey(a).toLowerCase()
      return label.includes(q) || key.includes(q)
    })
  }, [apps, search])

  const connectedAppKeys = useMemo(
    () => new Set(integrations.map((i) => i.appId.toLowerCase())),
    [integrations],
  )

  const handleDelete = async (integration: ComposioIntegration) => {
    const label = integration.displayName || integration.appId
    if (!confirm(`Disconnect ${label}? This cannot be undone.`)) return
    try {
      setDeletingId(integration.id)
      await composioApi.deleteConnection(integration.id)
      setIntegrations((prev) => prev.filter((i) => i.id !== integration.id))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to disconnect')
    } finally {
      setDeletingId(null)
    }
  }

  const [connectingKey, setConnectingKey] = useState<string | null>(null)

  const handleConnectClick = async (app: ComposioApp) => {
    const appKey = getAppKey(app)
    if (!appKey) return

    try {
      setConnectingKey(appKey)
      const { url, connectionId } = await composioApi.getOAuthUrl(appKey)

      // Open the provider's consent screen in a new tab so the user keeps
      // their session in this one. Composio's hosted flow handles the
      // provider callback server-side; when the tab closes or we detect
      // an active status, we promote the pending integration.
      const authWindow = window.open(url, '_blank', 'noopener,noreferrer')
      if (!authWindow) {
        alert(
          'Your browser blocked the popup. Please allow popups for this site and try again.',
        )
        setConnectingKey(null)
        return
      }

      // Poll finalize endpoint. Backend queries Composio for the connection
      // status and flips the DB row to active once the user authorizes.
      const maxAttempts = 60 // ~2 minutes at 2s intervals
      let attempts = 0
      const poll = async () => {
        attempts += 1
        try {
          const result = await composioApi.finalizeOAuth(connectionId)
          if (result.active) {
            setConnectingKey(null)
            await loadAll()
            return
          }
        } catch {
          // swallow — likely the connection is still being initialized
        }
        if (attempts >= maxAttempts) {
          setConnectingKey(null)
          await loadAll()
          return
        }
        setTimeout(poll, 2000)
      }
      setTimeout(poll, 2000)
    } catch (err) {
      alert(
        err instanceof Error ? err.message : 'Failed to start OAuth flow',
      )
      setConnectingKey(null)
    }
  }

  return (
    <div className="space-y-6">
      <StatusBanner
        status={status}
        message={statusMessage}
        onRefresh={loadAll}
        refreshing={refreshing}
      />

      <ConnectedIntegrations
        integrations={integrations}
        error={integrationsError}
        deletingId={deletingId}
        onDelete={handleDelete}
      />

      <AvailableApps
        apps={filteredApps}
        total={apps.length}
        search={search}
        onSearch={setSearch}
        connectedAppKeys={connectedAppKeys}
        disabled={status !== 'ok'}
        onConnect={handleConnectClick}
        connectingKey={connectingKey}
      />
    </div>
  )
}

// ============================================================================
// STATUS BANNER
// ============================================================================

function StatusBanner({
  status,
  message,
  onRefresh,
  refreshing,
}: {
  status: BackendStatus
  message: string | null
  onRefresh: () => void
  refreshing: boolean
}) {
  const variants: Record<
    BackendStatus,
    {
      icon: React.ReactNode
      title: string
      body: string
      borderColor: string
      bg: string
    }
  > = {
    checking: {
      icon: <Loader2 className="w-4 h-4 animate-spin" />,
      title: 'Checking Composio backend…',
      body: 'Contacting the Composio API via the server.',
      borderColor: 'var(--color-border)',
      bg: 'var(--color-bg-secondary)',
    },
    ok: {
      icon: <CheckCircle2 className="w-4 h-4 text-success-fg" />,
      title: 'Composio backend connected',
      body: 'Server is able to reach Composio and list apps.',
      borderColor: 'var(--color-border)',
      bg: 'var(--color-bg-card)',
    },
    'not-configured': {
      icon: <AlertTriangle className="w-4 h-4 text-warning-fg" />,
      title: 'COMPOSIO_API_KEY is not set',
      body:
        'Add COMPOSIO_API_KEY to apps/api/.env (see .env.example) and restart the API server.',
      borderColor: 'var(--color-border)',
      bg: 'var(--color-warning-bg, #fbead3)',
    },
    error: {
      icon: <AlertTriangle className="w-4 h-4 text-danger-fg" />,
      title: 'Unable to reach Composio',
      body: message || 'Unknown error contacting Composio.',
      borderColor: 'var(--color-border)',
      bg: 'var(--color-danger-bg, #fbdede)',
    },
  }

  const v = variants[status]

  return (
    <Card style={{ backgroundColor: v.bg, borderColor: v.borderColor }}>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{v.icon}</div>
          <div>
            <CardTitle className="text-base">{v.title}</CardTitle>
            <CardDescription className="mt-1">
              {v.body}
              {status === 'error' && message ? (
                <span className="block mt-1 font-mono text-xs text-text-muted">
                  {message}
                </span>
              ) : null}
            </CardDescription>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={refreshing}
        >
          {refreshing ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          )}
          Refresh
        </Button>
      </CardHeader>
    </Card>
  )
}

// ============================================================================
// CONNECTED INTEGRATIONS
// ============================================================================

function ConnectedIntegrations({
  integrations,
  error,
  deletingId,
  onDelete,
}: {
  integrations: ComposioIntegration[]
  error: string | null
  deletingId: string | null
  onDelete: (integration: ComposioIntegration) => void
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Plug className="w-4 h-4 text-market" />
          <CardTitle className="text-base">Connected integrations</CardTitle>
          <Badge variant="secondary" className="ml-auto">
            {integrations.length}
          </Badge>
        </div>
        <CardDescription>
          External apps this account has authorized through Composio.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="text-sm text-danger-fg">{error}</div>
        ) : integrations.length === 0 ? (
          <EmptyState
            title="No integrations yet"
            body="Once you connect an app below it will appear here."
          />
        ) : (
          <ul className="divide-y divide-border">
            {integrations.map((integration) => {
              const label = integration.displayName || integration.appId
              const isDeleting = deletingId === integration.id
              return (
                <li
                  key={integration.id}
                  className="flex items-center gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{label}</span>
                      <code className="font-mono text-xs text-text-muted truncate">
                        {integration.appId}
                      </code>
                      {integration.isActive === false ? (
                        <Badge variant="outline">inactive</Badge>
                      ) : null}
                      {integration.lastErrorAt ? (
                        <Badge className="bg-danger-bg text-danger-fg border-0">
                          error
                        </Badge>
                      ) : null}
                    </div>
                    <div className="text-xs text-text-muted mt-0.5 truncate">
                      Connection {integration.connectionId.slice(0, 16)}… ·
                      added {new Date(integration.createdAt).toLocaleDateString()}
                      {integration.lastSyncedAt
                        ? ` · last synced ${new Date(
                            integration.lastSyncedAt,
                          ).toLocaleDateString()}`
                        : ''}
                    </div>
                    {integration.lastErrorMessage ? (
                      <div className="text-xs text-danger-fg mt-0.5 truncate">
                        {integration.lastErrorMessage}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isDeleting}
                    onClick={() => onDelete(integration)}
                  >
                    {isDeleting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                    <span className="sr-only">Disconnect {label}</span>
                  </Button>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// AVAILABLE APPS
// ============================================================================

function AvailableApps({
  apps,
  total,
  search,
  onSearch,
  connectedAppKeys,
  disabled,
  onConnect,
  connectingKey,
}: {
  apps: ComposioApp[]
  total: number
  search: string
  onSearch: (v: string) => void
  connectedAppKeys: Set<string>
  disabled: boolean
  onConnect: (app: ComposioApp) => void | Promise<void>
  connectingKey: string | null
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">Available apps</CardTitle>
          <Badge variant="secondary" className="ml-auto">
            {total}
          </Badge>
        </div>
        <CardDescription>
          Apps you can connect via Composio. OAuth flow to be wired up —
          connecting opens the Composio dashboard for now.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search apps (e.g. slack, github, linear)…"
            disabled={disabled}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-bg-card focus:outline-none focus:ring-2 focus:ring-market/30 disabled:opacity-50"
          />
        </div>

        {disabled ? (
          <EmptyState
            title="Catalog unavailable"
            body="Configure Composio above to browse the app catalog."
          />
        ) : apps.length === 0 ? (
          <EmptyState
            title="No apps match that search"
            body="Try a different keyword, or clear the search."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {apps.map((app) => {
              const key = getAppKey(app)
              const label = getAppLabel(app)
              const description = getAppDescription(app)
              const logo = getAppLogo(app)
              const isConnected = connectedAppKeys.has(key.toLowerCase())
              return (
                <div
                  key={key || label}
                  className="flex items-start gap-3 p-3 rounded-xl border border-border bg-bg-card"
                >
                  {logo ? (
                    <img
                      src={logo}
                      alt=""
                      className="w-8 h-8 rounded-md object-contain flex-shrink-0 bg-bg-secondary"
                      loading="lazy"
                    />
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{label}</span>
                      {isConnected ? (
                        <Badge className="bg-success-bg text-success-fg border-0">
                          connected
                        </Badge>
                      ) : null}
                    </div>
                    {description ? (
                      <p className="text-xs text-text-muted mt-1 line-clamp-2">
                        {description}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onConnect(app)}
                    disabled={disabled || connectingKey === key}
                  >
                    {connectingKey === key ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {connectingKey === key
                      ? 'Waiting…'
                      : isConnected
                        ? 'Reconnect'
                        : 'Connect'}
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// EMPTY STATE
// ============================================================================

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="rounded-xl border border-dashed border-border px-4 py-8 text-center"
      style={{ backgroundColor: 'var(--color-bg-secondary)' }}
    >
      <p className="font-medium">{title}</p>
      <p className="text-sm text-text-muted mt-1">{body}</p>
    </div>
  )
}
